import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * Merge class names, resolving Tailwind conflicts.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS CONFIGURED RATHER THAN PLAIN `twMerge`
 * ---------------------------------------------------------------------------
 *
 * tailwind-merge decides which classes conflict by CLASSIFYING them against
 * stock Tailwind. Every name our preset adds is unknown to it — `text-default`
 * is not a stock colour, `text-body` is not a stock size — so it guesses, and
 * when it guesses that two classes share a group it silently keeps only the
 * last one.
 *
 * That is not theoretical. Renaming the shared table's `text-xs` to the
 * role-named `text-body` made tailwind-merge read `text-body` as a COLOUR, so
 * it looked like a conflict with `text-default` and dropped it — and every
 * column header in the product rendered pure black on the dark surface. Pure
 * black is not a value in our palette; it is the CSS initial colour, which is
 * what an element falls back to when its colour class was dropped on the way
 * in.
 *
 * The mirror-image case is latent rather than observed: `text-default` is
 * equally unknown, so in the other order it can be read as a size and lose to
 * `text-xs`. Both directions are covered below and by the tests.
 *
 * The failure is invisible to every other check we have. The CSS is correct.
 * The variable resolves. The token measurement passes, because it reads the
 * stylesheet rather than the element. The class simply never arrives, nothing
 * errors, and the class list just comes out one shorter.
 *
 * ---------------------------------------------------------------------------
 * WHY THESE LISTS ARE LITERAL
 * ---------------------------------------------------------------------------
 *
 * Reading them from the preset would be better, and does not work: the preset
 * is CommonJS and this module is bundled for the browser, where the import
 * yields no default export and takes the whole app down.
 *
 * So they are literal, and `cn.test.ts` asserts they still match the preset.
 * A name added to the preset without being added here fails that test — which
 * is the only thing standing between this and the silent drift that caused the
 * original defect.
 */

/** Sizes the preset adds that stock Tailwind has no equivalent for. */
export const CUSTOM_FONT_SIZES = [
	'title',
	'heading',
	'prose',
	'body',
	'label',
	'meta',
];

/** Radii the preset adds. */
export const CUSTOM_RADII = ['control', 'card', 'pill'];

/**
 * Colour names the preset adds. Declared so `text-default` is understood as a
 * colour rather than guessed at — the mirror image of the size problem above.
 */
export const CUSTOM_TEXT_COLORS = [
	'default',
	'muted',
	'subtle',
	'faint',
	'on-inverse',
	'on-fill',
	'accent',
	'accent-hover',
	'accent-tint-fg',
	'on-accent',
	'success',
	'success-tint-fg',
	'on-success',
	'danger',
	'danger-strong',
	'danger-tint-fg',
	'warning',
	'warning-tint-fg',
	'on-warning',
	'neutral-tint-fg',
	'nav',
	'nav-active',
	'nav-muted',
	'nav-strong',
	'primary',
	'secondary',
	'inverse',
	...Array.from({ length: 16 }, (_, index) => `chip-${String(index + 1)}`),
];

const twMerge = extendTailwindMerge({
	extend: {
		classGroups: {
			'font-size': [{ text: CUSTOM_FONT_SIZES }],
			'text-color': [{ text: CUSTOM_TEXT_COLORS }],
			rounded: [{ rounded: CUSTOM_RADII }],
		},
	},
});

export function cn(...inputs: Array<ClassValue>): string {
	return twMerge(clsx(inputs));
}
